import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Services
import { NavigationParam, RouteParam, ServiceBase } from '../service-base';

// Modèles
import { SalesInvoice } from 'src/app/@shared/models/sales/invoice/sales-invoice.model';
import { SalesInvoiceAllocation } from 'src/app/@shared/models/sales/invoice/sales-invoice-allocation.model';
import { File as FileModel } from 'src/app/@shared/models/file/file.model';

// Utilitaires
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';
import { ODataListResponse } from 'src/app/@shared/utilities/odata/response';


const CLASS_NAME = 'SalesInvoice';
const ENDPOINT = `/sales-invoices/`;
const NAVIGATIONS: NavigationParam[] = [
    { key: 'account-entries', path: 'account-entries/', childClass: 'AccountEntryLine' },
    { key: 'allocations', path: 'allocations/', childClass: 'SalesInvoiceAllocation' },
    { key: 'logo', path: 'logo/', childClass: 'File', resultCardinality: 'one' },
    { key: 'potential-settlements', path: 'potential-settlements/', childClass: 'Settlement' },
    { key: 'settlements', path: 'settlements/', childClass: 'Settlement' },
    { key: 'quotations', path: 'quotations/', childClass: 'Quotation' },
]
const ROUTES: RouteParam[] = [
    { key: 'abandonOcr', path: '{id}/abandon-ocr/' },
    { key: 'cancel', path: '{id}/cancel/' },
    { key: 'confirmIsInvoice', path: '{id}/confirm-is-invoice/' },
    { key: 'creditNote', path: '{id}/credit-note/' },
    { key: 'customerLabel', path: '{id}/customer-label/' },
    { key: 'devalidate', path: '{id}/devalidate/' },
    { key: 'downloadFile', path: '{id}/pdf/' },
    { key: 'duplicate', path: '{id}/duplicate/' },
    { key: 'export', path: 'export/' },
    { key: 'markAsSent', path: '{id}/mark-as-sent/' },
    { key: 'massDelete', path: 'mass-delete/' },
    { key: 'massDownload', path: 'mass-download/' },
    { key: 'modify', path: '{id}/modify/' },
    { key: 'proposeAllocations', path: '{id}/propose-allocations/' },
    { key: 'remind', path: '{id}/remind/' },
    { key: 'replaceFile', path: '{id}/replace-file/' },
    { key: 'retryOcr', path: '{id}/retry-ocr/' },
    { key: 'send', path: '{id}/send/' },
    { key: 'validate', path: '{id}/validate/' },
    { key: 'writeOff', path: '{id}/write-off/' },
]

@Injectable({
    providedIn: 'root'
})
export class SalesInvoiceService extends ServiceBase {

    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addNavigations(NAVIGATIONS);
        this.addRoutes(ROUTES);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public abandonOcr$(id: number): Observable<SalesInvoice> {
        const url = this.routes['abandonOcr'].replace('{id}', id.toString());
        return this._postAction$<SalesInvoice>(url);
    }

    public cancel$(invoice: SalesInvoice): Observable<SalesInvoice> {
        let url = this.routes.cancel.replace('{id}', invoice.id.toString());
        return this.update$(invoice, {
            url: url
        });
    }

    public confirmIsInvoice$(id: number): Observable<SalesInvoice> {
        const url = this.routes['confirmIsInvoice'].replace('{id}', id.toString());
        return this._postAction$<SalesInvoice>(url);
    }

    public creditNote$(invoice: SalesInvoice): Observable<SalesInvoice> {
        let url = this.routes.creditNote.replace('{id}', invoice.id.toString());
        return this.update$(invoice, {
            url: url
        });
    }

    /**
     * Écarte le libellé client lu par l'OCR : l'utilisateur décline la proposition,
     * qui ne reviendra plus pour ce libellé (marqueur posé sur le client de la facture).
     */
    public dismissCustomerLabel$(invoice: SalesInvoice): Observable<SalesInvoice> {
        const url = this.routes['customerLabel'].replace('{id}', invoice.id.toString());
        return this._deleteAction$<SalesInvoice>(url);
    }

    public duplicate$(invoice: SalesInvoice): Observable<SalesInvoice> {
        let url = this.routes.duplicate.replace('{id}', invoice.id.toString());
        return this.update$(invoice, {
            url: url
        });
    }

    public getAllocations$(id: number): Observable<SalesInvoiceAllocation[]> {
        return this.getRelated$({ id }, 'allocations', {
            expands: ['account'],
            selects: [
                'account/account',
                'account/label',
                'aiCreated',
                'aiExplanation',
                'amount',
                'label',
            ],
        });
    }

    public getLogo$(invoice: any): Observable<FileModel> {
        return this.getRelated$(invoice, 'logo');
    }

    public markAsSent$(invoice: SalesInvoice): Observable<SalesInvoice> {
        let url = this.routes.markAsSent.replace('{id}', invoice.id.toString());
        return this.update$(invoice, {
            url: url
        });
    }

    public modify$(invoice: SalesInvoice): Observable<SalesInvoice> {
        let url = this.routes.modify.replace('{id}', invoice.id.toString());
        return this.update$(invoice, {
            url: url
        });
    }

    public proposeAllocations$(id: number): Observable<SalesInvoice> {
        const url = this.routes['proposeAllocations'].replace('{id}', id.toString());
        return this._postAction$<SalesInvoice>(url);
    }

    /**
     * Retient le libellé client lu par l'OCR sur la fiche du client de la facture :
     * les prochaines factures portant ce libellé désigneront ce client.
     * Refusé (400) si le libellé est déjà retenu pour un autre client du dossier.
     */
    public rememberCustomerLabel$(invoice: SalesInvoice): Observable<SalesInvoice> {
        const url = this.routes['customerLabel'].replace('{id}', invoice.id.toString());
        return this._postAction$<SalesInvoice>(url);
    }

    public remind$(invoice: SalesInvoice): Observable<SalesInvoice> {
        let url = this.routes.remind.replace('{id}', invoice.id.toString());
        return this.update$(invoice, {
            url: url
        });
    }

    public replaceFile$(id: number, attachment: string, attachmentName: string): Observable<SalesInvoice> {
        const url = this.routes['replaceFile'].replace('{id}', id.toString());
        return this._postAction$<SalesInvoice>(url, { attachment, attachmentName });
    }

    public retryOcr$(id: number): Observable<SalesInvoice> {
        const url = this.routes['retryOcr'].replace('{id}', id.toString());
        return this._postAction$<SalesInvoice>(url);
    }

    /**
     * Remplace l'intégralité des imputations de la facture (annule et remplace).
     * POST de la liste complète sur `/sales-invoices/{id}/allocations/`,
     * la réponse renvoie la liste à jour.
     */
    public saveAllocations$(id: number, allocations: SalesInvoiceAllocation[]): Observable<SalesInvoiceAllocation[]> {
        const url = this.routes['single'].replace('{id}', id.toString()) + this.navigations['allocations'].path;
        const data = allocations.map(allocation => this._toApi(allocation));
        return this.httpClient.post(url, data).pipe(
            map((body: any) => {
                const response = new ODataListResponse(body);
                return response.results.map(
                    (result: any) => GeneralFactory.get('SalesInvoiceAllocation', result) as SalesInvoiceAllocation
                );
            }),
            catchError((error: any) => {
                this.handleError(error);
                return of([] as SalesInvoiceAllocation[]);
            })
        );
    }

    public send$(invoice: SalesInvoice): Observable<SalesInvoice> {
        let url = this.routes.send.replace('{id}', invoice.id.toString());
        return this.update$(invoice, {
            url: url
        });
    }

    public uploadLogo$(invoice: any, file: File): Observable<any> {
        let url = this.routes['single'].replace('{id}', invoice.id.toString()) + this.navigations['logo'].path;
        return this.uploadFile$(file, url);
    }

    public writeOff(invoice: SalesInvoice): void {
        this.update(invoice, {
            url: this.routes.writeOff.replace('{id}', invoice.id.toString())
        });
    }

}
